import React, { cloneElement, isValidElement } from 'react';
import { ActivityIndicator, Pressable } from 'react-native';
import type { ViewStyle } from 'react-native';

import { tokens } from '@design';

import type {
  IconButtonProps,
  IconButtonSize,
  IconButtonVariant,
} from './IconButton.types';

interface IconButtonStyleResult {
  container: ViewStyle;
  containerDisabled: ViewStyle;
  defaultIconColor: string;
  disabledIconColor: string;
  spinnerColor: string;
}

interface SizeTokens {
  side: number;
  padding: number;
  iconSize: number;
}

const SIZE_TOKENS: Readonly<Record<IconButtonSize, SizeTokens>> = {
  md: {
    side: tokens.spacing.absolute["11"],
    padding: tokens.spacing.absolute["2"],
    iconSize: 20,
  },
  sm: {
    side: tokens.spacing.absolute["8"],
    padding: tokens.spacing.absolute["1_5"],
    iconSize: 16,
  },
};

const styleCache = new Map<string, IconButtonStyleResult>();

function getIconButtonStyles(
  variant: IconButtonVariant,
  size: IconButtonSize,
): IconButtonStyleResult {
  const cacheKey = `${variant}:${size}`;
  const cached = styleCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const sizeTokens = SIZE_TOKENS[size];

  const container: ViewStyle = {
    width: sizeTokens.side,
    height: sizeTokens.side,
    padding: sizeTokens.padding,
    borderRadius: tokens.radii.absolute["radius-8"],
    alignItems: 'center',
    justifyContent: 'center',
  };

  let result: IconButtonStyleResult;

  switch (variant) {
    case 'primary':
      result = {
        container: {
          ...container,
          backgroundColor: tokens.colors.semantic.general["Main Color"],
        },
        containerDisabled: {
          backgroundColor: tokens.colors.raw.gray["300"],
        },
        defaultIconColor: tokens.colors.raw.white,
        disabledIconColor: tokens.colors.raw.white,
        spinnerColor: tokens.colors.raw.white,
      };
      break;

    case 'secondary':
      result = {
        container: {
          ...container,
          backgroundColor: tokens.colors.raw.white,
          borderWidth: tokens.spacing.absolute["px"],
          borderColor: tokens.colors.raw.gray["200"],
        },
        containerDisabled: {
          backgroundColor: tokens.colors.raw.gray["100"],
          borderColor: tokens.colors.raw.gray["200"],
        },
        defaultIconColor: tokens.colors.semantic.general.foreground,
        disabledIconColor: tokens.colors.raw.gray["400"],
        spinnerColor: tokens.colors.semantic.general.foreground,
      };
      break;

    case 'destructive':
      result = {
        container: {
          ...container,
          backgroundColor: tokens.colors.raw.red["500"],
        },
        containerDisabled: {
          backgroundColor: tokens.colors.raw.gray["300"],
        },
        defaultIconColor: tokens.colors.raw.white,
        disabledIconColor: tokens.colors.raw.white,
        spinnerColor: tokens.colors.raw.white,
      };
      break;

    case 'ghost':
      result = {
        container: {
          ...container,
          backgroundColor: 'transparent',
        },
        containerDisabled: {},
        defaultIconColor: tokens.colors.semantic.general.foreground,
        disabledIconColor: tokens.colors.raw.gray["400"],
        spinnerColor: tokens.colors.semantic.general.foreground,
      };
      break;
  }

  styleCache.set(cacheKey, result);
  return result;
}

/**
 * Apply default `size` and `color` to the icon via `cloneElement`. Caller-set
 * props on the icon win; defaults fill in only when undefined.
 *
 * Falls back to rendering the icon as-is if it is not a valid React element
 * (e.g., a string, null, or undefined) — a runtime no-op for invalid input,
 * since `IconButtonProps.icon` is typed as `ReactNode`.
 */
function applyIconDefaults(
  icon: React.ReactNode,
  derivedSize: number,
  derivedColor: string,
): React.ReactNode {
  if (!isValidElement(icon)) {
    return icon;
  }

  const existingProps = (icon.props ?? {}) as { size?: number; color?: string };

  return cloneElement(icon as React.ReactElement<{ size?: number; color?: string }>, {
    size: existingProps.size ?? derivedSize,
    color: existingProps.color ?? derivedColor,
  });
}

export function IconButton(props: IconButtonProps) {
  const {
    variant = 'secondary',
    size = 'md',
    icon,
    onPress,
    disabled = false,
    loading = false,
    accessibilityLabel,
    style,
    testID,
  } = props;

  const styles = getIconButtonStyles(variant, size);
  const sizeTokens = SIZE_TOKENS[size];
  const isInactive = disabled || loading;

  const iconColor = isInactive ? styles.disabledIconColor : styles.defaultIconColor;
  const renderedIcon = loading ? (
    <ActivityIndicator size={sizeTokens.iconSize} color={styles.spinnerColor} />
  ) : (
    applyIconDefaults(icon, sizeTokens.iconSize, iconColor)
  );

  return (
    <Pressable
      onPress={onPress}
      disabled={isInactive}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ disabled: isInactive, busy: loading }}
      testID={testID}
      style={({ pressed }) => [
        styles.container,
        isInactive && styles.containerDisabled,
        pressed && !isInactive && { opacity: 0.85 },
        style,
      ]}
    >
      {renderedIcon}
    </Pressable>
  );
}
